import { useEffect, useState } from "react";
import ApiService from "../../core/service/api-service";
import { Button, Space, Tag, Modal, Input, Form, Table } from "antd";
import { useTranslation } from "react-i18next";
import { dateFormat } from "../../core/utils/date-utils";
import { CheckCircleOutlined, HistoryOutlined } from "@ant-design/icons";
import DataGrid from "../../core/components/data-grid/DataGrid";
import { useDataGrid } from "../../core/components/data-grid/DataGridContext";

const { TextArea } = Input;

const StudyTaskTypeEnum = {
    1: 'MISSING_IMAGE',
    2: 'RE_ASSESSMENT',
    3: 'REMOVE_REPORT_CONFIRMATION'
}

const StudyTaskStatusEnum = {
    1: 'OPEN',
    2: 'RESOLVED',
    3: 'CANCEL'
}

export default function StudyTasksGrid({ studyId }) {
    const { setDataGridProps, queryModel, setData, setPaginationResult, refreshDataGrid } = useDataGrid()
    const [resolveModalVisible, setResolveModalVisible] = useState(false);
    const [historyModalVisible, setHistoryModalVisible] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [form] = Form.useForm();
    const { t } = useTranslation();

    const initDataGrid = async () => {
        const response = await ApiService.StudyTask.DataGrid({
            ...queryModel,
            filter: {
                studyId: studyId
            }
        });
        if (response.success) {
            setData(response.data)
            setPaginationResult(response.pagination)
        }
    }

    useEffect(() => {
        initDataGrid()
    }, [queryModel.updateKey])

    const handleResolve = async () => {
        try {
            const values = await form.validateFields();
            const response = await ApiService.StudyTask.Resolve({
                taskId: selectedTask.id,
                description: values.description
            });
            if (response.success) {
                setResolveModalVisible(false);
                setSelectedTask(null);
                form.resetFields();
                refreshDataGrid();
            }
        } catch (error) {
            console.error('Validation failed:', error);
        }
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 1: return 'orange'; // OPEN
            case 2: return 'green';  // RESOLVED
            case 3: return 'red';    // CANCEL
            default: return 'default';
        }
    }

    const loadHistory = async (task) => {
        setSelectedTask(task);
        setHistoryModalVisible(true);
        setHistoryLoading(true);
        try {
            const response = await ApiService.StudyTask.GetHistory(task.id);
            if (response.success) {
                setHistoryData(response.data);
            }
        } catch (error) {
            console.error('Failed to load history:', error);
        } finally {
            setHistoryLoading(false);
        }
    }

    const historyColumns = [
        {
            title: t('Date'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (text) => dateFormat(text)
        },
        {
            title: t('Old Status'),
            dataIndex: 'oldStatus',
            key: 'oldStatus',
            width: 100,
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {t(StudyTaskStatusEnum[status] || status)}
                </Tag>
            )
        },
        {
            title: t('New Status'),
            dataIndex: 'newStatus',
            key: 'newStatus',
            width: 100,
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {t(StudyTaskStatusEnum[status] || status)}
                </Tag>
            )
        },
        {
            title: t('User'),
            dataIndex: ['user', 'visibleName'],
            key: 'userName',
            width: 150,
        },
        {
            title: t('Description'),
            dataIndex: 'description',
            key: 'description',
            width: 200,
        },
    ];

    useEffect(() => {
        setDataGridProps({
            rowKey: 'id',
            enableRowSelection: false,
            antdTableProps: {
                scroll: {}
            },
            columns: [
                {
                    title: t('Date'),
                    dataIndex: 'createdAt',
                    key: 'createdAt',
                    width: 150,
                    render: (text) => dateFormat(text)
                },
                {
                    title: t('Type'),
                    dataIndex: 'taskType',
                    key: 'taskType',
                    width: 150,
                    render: (val) => t(StudyTaskTypeEnum[val] || val)
                },
                {
                    title: t('Status'),
                    dataIndex: 'status',
                    key: 'status',
                    width: 100,
                    render: (status) => (
                        <Tag color={getStatusColor(status)}>
                            {t(StudyTaskStatusEnum[status] || status)}
                        </Tag>
                    )
                },
                {
                    title: t('Created By'),
                    dataIndex: ['user', 'visibleName'],
                    key: 'userName',
                    width: 150,
                },
                {
                    title: t('Description'),
                    dataIndex: 'description',
                    key: 'description',
                    width: 200,
                },
                {
                    title: t('Actions'),
                    key: 'actions',
                    width: 150,
                    render: (_, record) => (
                        <Space>
                            {record.status === 1 && (
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<CheckCircleOutlined />}
                                    onClick={() => {
                                        setSelectedTask(record);
                                        setResolveModalVisible(true);
                                    }}
                                >
                                    {t('Resolve')}
                                </Button>
                            )}
                            <Button
                                size="small"
                                icon={<HistoryOutlined />}
                                onClick={() => loadHistory(record)}
                            >
                                {t('History')}
                            </Button>
                        </Space>
                    )
                },
            ]
        })
    }, [])

    return (
        <>
            <DataGrid
                name={'study-tasks'}
                mobileCardTitle={(record) => {
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontWeight: 600, fontSize: 13 }}>{t(StudyTaskTypeEnum[record.taskType])}</span>
                                <Tag color={getStatusColor(record.status)}>
                                    {t(StudyTaskStatusEnum[record.status])}
                                </Tag>
                            </div>
                            <div style={{ fontSize: 12, color: '#666' }}>
                                {t('Created By')}: {record.user?.visibleName}
                            </div>
                            <div style={{ fontSize: 11, color: '#888' }}>
                                {dateFormat(record.createdAt)}
                            </div>
                            {record.description && (
                                <div style={{ fontSize: 11, color: '#999' }}>
                                    {record.description}
                                </div>
                            )}
                        </div>
                    );
                }}
            />
            <Modal
                title={t('Resolve Task')}
                open={resolveModalVisible}
                onCancel={() => {
                    setResolveModalVisible(false);
                    setSelectedTask(null);
                    form.resetFields();
                }}
                onOk={handleResolve}
                okText={t('Resolve')}
                cancelText={t('Cancel')}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="description"
                        label={t('Resolution Description')}
                        rules={[{ required: true, message: t('Please enter resolution description') }]}
                    >
                        <TextArea rows={4} placeholder={t('Enter resolution description')} />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                title={t('Task History')}
                open={historyModalVisible}
                onCancel={() => {
                    setHistoryModalVisible(false);
                    setSelectedTask(null);
                    setHistoryData([]);
                }}
                footer={null}
                width={800}
            >
                <Table
                    rowKey="id"
                    columns={historyColumns}
                    dataSource={historyData}
                    loading={historyLoading}
                    pagination={false}
                    size="small"
                />
            </Modal>
        </>
    )
}
